import React, { useEffect, useRef, useState } from 'react';
import './Hero.css';

// ============= 数字滚动 Hook =============
function useCountUp(target: number, duration = 1400, start = true) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const startTime = performance.now();
    const from = 0;

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const p = Math.min(1, (now - startTime) / duration);
      const v = from + (target - from) * easeOut(p);
      setValue(v);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration, start]);

  return value;
}

// ============= 数据卡 =============
interface HeroStat {
  num: number;
  suffix: string;
  label: string;
  icon: string;
  hero?: boolean;
  decimals?: number;
  /** 直接展示字面数字，不做 CountUp（用于 "0→1" 这种复合数字） */
  literal?: string;
}

const stats: HeroStat[] = [
  { num: 6,   suffix: '年+',    label: 'KA 云计算·AI 售前经验',              icon: 'fa-clock' },
  { num: 100, suffix: '倍',     label: '3年单客户业绩增长 · 30万 → 3000万+', icon: 'fa-chart-line', hero: true },
  { num: 8,   suffix: '万点+',  label: '桌面云单项目最大规模 · 行业最大',      icon: 'fa-server' },
  { num: 1,   suffix: '',       label: 'AI Coding 公司首单 · 央企标杆项目',   icon: 'fa-flag-checkered', literal: '0→1' },
];

const chips = [
  { icon: 'fa-cloud',           label: '云计算 / 私有云' },
  { icon: 'fa-brain',           label: 'AI / 大模型 / Agent' },
  { icon: 'fa-users',           label: 'KA 大客户经营' },
  { icon: 'fa-diagram-project', label: '解决方案架构设计' },
  { icon: 'fa-bullseye',        label: '战役沉淀' },
];

// ============= 单个 Stat 卡（含 spotlight） =============
const StatCard: React.FC<{ stat: HeroStat; index: number; started: boolean }> = ({
  stat, index, started,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const displayed = useCountUp(stat.num, 1400 + index * 120, started);

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    el.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <div
      ref={cardRef}
      className={`hero-stat animate-in-stat${stat.hero ? ' is-hero' : ''}`}
      style={{ animationDelay: `${700 + index * 140}ms` }}
      role="listitem"
      onMouseMove={handleMove}
    >
      {/* 进场后的橙色 sweep 内高光 */}
      <span
        className="stat-sweep"
        aria-hidden
        style={{ animationDelay: `${900 + index * 140}ms` }}
      ></span>
      <div className="hero-stat-icon">
        <i className={`fas ${stat.icon}`}></i>
      </div>
      <div className="hero-stat-num">
        {stat.literal ? (
          <span className="hero-stat-num-literal">{stat.literal}</span>
        ) : (
          <>
            {Math.round(displayed)}
            <span className="hero-stat-num-suffix">{stat.suffix}</span>
          </>
        )}
      </div>
      <div className="hero-stat-label">{stat.label}</div>
    </div>
  );
};

// ============= 按钮 Ripple =============
const attachRipple = (e: React.MouseEvent<HTMLElement>) => {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  const circle = document.createElement('span');
  const diameter = Math.max(rect.width, rect.height);
  circle.style.width = circle.style.height = `${diameter}px`;
  circle.style.left = `${e.clientX - rect.left - diameter / 2}px`;
  circle.style.top = `${e.clientY - rect.top - diameter / 2}px`;
  circle.className = 'ripple';
  el.querySelectorAll('.ripple').forEach((n) => n.remove());
  el.appendChild(circle);
  setTimeout(() => circle.remove(), 700);
};

// ============= Hero =============
interface HeroProps {
  onScrollToContent?: () => void;
}

export const Hero: React.FC<HeroProps> = ({ onScrollToContent }) => {
  // 页面加载后立即启动数字动画
  const [started, setStarted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStarted(true), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="hero" data-testid="hero-section">
      {/* 首屏整页扫光带（只播一次） */}
      <span className="hero-sweep" aria-hidden></span>

      <div className="hero-inner">
        {/* 顶部信息条 */}
        <div className="hero-topbar animate-in-slide-l" style={{ animationDelay: '0ms' }}>
          <div className="hero-brand">
            <span className="hero-brand-dot"></span>
            <span>DONGJUN.TECH · PERSONAL PORTAL</span>
          </div>
          <div className="hero-status">考虑机会中 · 深圳</div>
        </div>

        {/* 主体：左文案 + 右头像 */}
        <div className="hero-main">
          <div className="hero-content">
            <div className="hero-eyebrow animate-in-slide-l" style={{ animationDelay: '120ms' }}>
              <span className="hero-eyebrow-badge">SA</span>
              <span>SOLUTION ARCHITECT · CLOUD × AI</span>
            </div>

            <h1
              className="hero-title animate-in-clip"
              style={{ animationDelay: '250ms' }}
              data-testid="page-title"
            >
              <span className="hero-title-name">董俊</span>
              <span className="hero-title-sep">·</span>
              <span className="hero-title-role">解决方案架构师</span>
            </h1>

            <p className="hero-subtitle animate-in" style={{ animationDelay: '450ms' }}>
              6 年 KA 大客户售前经验，主导公司最大规模桌面云、AI Coding 首单、AI 客户共建等标杆项目。
              擅长关键客户突破、高层汇报、新场景打法探索。
            </p>

            {/* 能力标签墙 */}
            <div className="hero-chips">
              {chips.map((c, i) => (
                <span
                  key={i}
                  className="hero-chip animate-in-chip"
                  style={{ animationDelay: `${600 + i * 90}ms` }}
                >
                  <i className={`fas ${c.icon}`}></i>
                  {c.label}
                </span>
              ))}
            </div>

            <div className="hero-actions animate-in" style={{ animationDelay: '1100ms' }}>
              <button
                className="btn-accent"
                onClick={(e) => { attachRipple(e); onScrollToContent?.(); }}
                type="button"
              >
                <i className="fas fa-arrow-down"></i>
                查看核心能力
              </button>
              <a className="btn-ghost" href="mailto:970354314@qq.com" onClick={attachRipple}>
                <i className="fas fa-envelope"></i>
                Email
              </a>
              <a
                className="btn-ghost"
                href="https://github.com/dongjun-dj"
                target="_blank"
                rel="noreferrer noopener"
                onClick={attachRipple}
              >
                <i className="fab fa-github"></i>
                GitHub
              </a>
              <a
                className="btn-ghost"
                href="/resume/dongjun-resume.pdf"
                target="_blank"
                rel="noreferrer noopener"
                onClick={attachRipple}
              >
                <i className="fas fa-file-pdf"></i>
                简历 PDF
              </a>
            </div>
          </div>

          {/* 头像卡：环形装饰 + 轨道点 + 首字母占位 */}
          <div className="hero-avatar-wrap animate-in-scale" style={{ animationDelay: '500ms' }}>
            <span className="hero-avatar-orbit-dot" aria-hidden></span>
            <div className="hero-avatar" aria-label="董俊头像">
              <img src="/images/avatar.jpg" alt="董俊" />
            </div>
            <div className="hero-avatar-tag">DONGJUN</div>
          </div>
        </div>

        {/* 数字大字报 */}
        <div className="hero-stats" role="list" aria-label="核心数据">
          {stats.map((s, i) => (
            <StatCard key={i} stat={s} index={i} started={started} />
          ))}
        </div>
      </div>

      <div className="hero-scroll-hint">
        <i className="fas fa-chevron-down"></i>
        <span>SCROLL</span>
        <i className="fas fa-chevron-down"></i>
      </div>
    </section>
  );
};

export default Hero;
